import { SlDropdownMenuOption, useStacklineTheme } from '@stackline/ui';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import React, { useCallback, useMemo, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useUnifiedTrafficData } from 'src/components/AtlasV2Components/Hooks/useUnifiedTrafficData';
import { EntityRecord } from 'src/components/AtlasV2Components/utils/brandDataUtils';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import AllBrandsTooltip from 'src/components/BeaconV2Components/TopEntityChartV2/StackedBarChartComponent/AllBrandsTooltip';
import StackedTopEntityChart from 'src/components/BeaconV2Components/TopEntityChartV2/StackedTopEntityChart';
import { safeDivide } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  useMetricFormatter,
  useAppSelector,
  useMainTimePeriodShortDisplayName,
  useComparisonTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import { useCategoryIds, useMainEntityType, useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import BrandFilterPills from 'src/components/BeaconV2Components/TopEntityChartV2/StackedBarChartComponent/BrandFilterPills';
import { SeriesColumnOptions } from 'highcharts';
import TopEntityChartV2, {
  TopEntityV2ChartProps
} from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { useStackedChartExportData } from 'src/components/AtlasV2Components/Hooks/useStackedChartExportData';
import { StackedChartDataSet } from 'src/components/AtlasV2Components/utils/chartExportUtils';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useBrandTrafficStore } from 'src/zustandStores/BrandTrafficStore';
import { ValueOf } from 'sl-api-connector';
import { ChevronWithPeriod } from 'src/components/SvgIcons';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

const KEYWORD_LIMIT = 500;
const ENTITY_LIMIT = 500;

// Types
type MetricDropdownOption = {
  id: string;
  label: string;
  apiMetricFieldName: string;
  /** If percentage is visible, the stacked bar chart will increased to 100% */
  isPercentageVisible?: boolean;
  /** If a default metric is provided, the stacked bar chart will use the default metric
   * Example: 'totalTrafficPercent' will use the totalTraffic percent as the default metric */
  defaultMetric?: string;
  subtitleLabel?: string;
  metricTypeDisplay?: ValueOf<typeof METRICTYPE>;
};

type GroupByField = {
  id: string;
  label: string;
};

/**
 * Custom hook for handling entity display names
 */
const useEntityDisplayName = (selectedGroupByField: GroupByField) => {
  const categories = useAppSelector((state) => state.categories);
  const subCategories = useAppSelector((state) => state.subCategories);

  return useCallback(
    (fieldKey: string | number): string => {
      if (selectedGroupByField.id === 'keyword') {
        return fieldKey as string;
      } else if (selectedGroupByField.id === 'category' && categories) {
        const category = categories.find((cat) => cat.categoryId === Number(fieldKey));
        return category?.name || `Category ${fieldKey}`;
      } else if (selectedGroupByField.id === 'subcategory' && subCategories) {
        const subcategory = subCategories.find((subcat) => subcat.subCategoryId === Number(fieldKey));
        // Use type assertion to bypass TypeScript type checking
        const subcatAny = subcategory as any;
        return subcatAny?.name || subcatAny?.subCategoryName || `Subcategory ${fieldKey}`;
      } else if (selectedGroupByField.id === 'topic') {
        return `Topic ${fieldKey}`;
      }
      return String(fieldKey);
    },
    [selectedGroupByField.id, categories, subCategories]
  );
};

/**
 * Component that displays a filterable stacked bar chart for traffic data
 * Supports grouping by different entity types (keyword, category, subcategory, topic)
 */
const AtlasV2FilterableStackedBarChart = ({
  groupByFieldOptions,
  metricDropdownOptions
}: {
  groupByFieldOptions: GroupByField[];
  metricDropdownOptions: MetricDropdownOption[];
}) => {
  // State
  const [isPercentageView, setIsPercentageView] = useState<boolean>(false);
  const [selectedMetricField, setSelectedMetricField] = useState<MetricDropdownOption>(metricDropdownOptions[0]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<'current' | 'comparison'>('current');
  const [selectedGroupByField, setSelectedGroupByField] = useState<GroupByField>(groupByFieldOptions[0]);
  const [showBrands, setShowBrands] = useState<boolean>(true);
  const nonPercentageMetricDropdownOptions = metricDropdownOptions.filter((option) => !option.isPercentageVisible);

  // Hooks
  const theme = useStacklineTheme();
  const formatMetric = useMetricFormatter();
  const mainTimePeriodName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodName = useComparisonTimePeriodShortDisplayName();
  const { displayName: mainTimePeriodDisplayName } = useAppSelector((state) => state.mainTimePeriod);
  const { displayName: comparisonTimePeriodDisplayName } = useAppSelector((state) => state.comparisonTimePeriod);
  const entityPhrase = useEntityPhrasing();
  const mainEntityType = useMainEntityType();
  const getEntityDisplayName = useEntityDisplayName(selectedGroupByField);
  const retailerId = useRetailerId();
  const categoryIds = useCategoryIds();
  const taxonomyText = useTaxonomySubtitleText();
  // Get selectedBrands from store first
  const { selectedBrands: currentSelectedBrands, userChangedBrands, setUserChangedBrands } = useBrandTrafficStore();

  // Use the unified traffic data hook
  const {
    isLoadingKeywords,
    isLoadingBrands,
    getBrandColor,
    pickTop4BrandsByVolume,
    entityTrafficData,
    isLoadingEntityTraffic,
    isLoadingComparisonEntityTraffic,
    isLoadingBrandEntityTraffic,
    isLoadingComparisonBrandEntityTraffic,
    entitiesForNonBrandChart,
    comparisonEntitiesForNonBrandChart,
    totalEntityTrafficMap,
    totalComparisonEntityTrafficMap,
    groupedBrandData
  } = useUnifiedTrafficData({
    retailerId: retailerId ? +retailerId : undefined,
    categoryIds,
    enabled: true,
    keywordLimit: KEYWORD_LIMIT,
    entityLimit: ENTITY_LIMIT,
    apiMetricFieldName: selectedMetricField.apiMetricFieldName,
    selectedGroupByField,
    selectedTimePeriod,
    selectedBrands: currentSelectedBrands
  });

  // Prepare chart series data
  const chartSeries = useMemo(() => {
    if (groupedBrandData?.length === 0) return [];

    const entityOrder = groupedBrandData[0]?.keywordOrder || [];

    const entityIndices = new Map();
    entityOrder.forEach((entity, index) => {
      entityIndices.set(entity, index);
    });

    const stackedData: StackedChartDataSet[] = [];

    // Use the appropriate traffic map based on selectedTimePeriod
    const totalTrafficMap =
      selectedTimePeriod === 'comparison' ? totalComparisonEntityTrafficMap : totalEntityTrafficMap;

    if (totalTrafficMap && entityOrder.length > 0) {
      const otherBrandsColor = theme.colors.primary;
      const otherBrandsData = entityOrder.map((entity) => {
        // Get total traffic for this entity
        const totalTraffic = totalTrafficMap.get(entity) || 0;

        // Sum traffic from all selected brands for this entity
        const selectedBrandsSum = groupedBrandData.reduce((sum, brand) => {
          const entityIdx = entityIndices.get(entity);

          return sum + (entityIdx !== undefined ? brand.keywordVolume[entityIdx] || 0 : 0);
        }, 0);

        const value = Math.max(0, totalTraffic - selectedBrandsSum);

        return {
          y: value,
          rawValue: value,
          color: otherBrandsColor
        };
      });

      // Only add "Other Brands" if there's meaningful data to show
      if (otherBrandsData.some((point) => point.y > 0)) {
        stackedData.push({
          name: 'Other Brands',
          data: otherBrandsData,
          color: otherBrandsColor
        });
      }
    }

    groupedBrandData.forEach((brand, index) => {
      const color = getBrandColor(brand.brandId, index);

      const dataPoints = entityOrder.map((entity) => {
        const entityIdx = entityIndices.get(entity);
        const value = entityIdx !== undefined ? brand.keywordVolume[entityIdx] || 0 : 0;

        return {
          y: value,
          rawValue: value,
          color
        };
      });

      stackedData.push({
        name: brand.brandName,
        data: dataPoints,
        color,
        totalVolume: brand.totalTraffic
      });
    });

    stackedData.sort((a, b) => a.totalVolume - b.totalVolume);

    if (isPercentageView && stackedData.length > 0) {
      const totals = entityOrder.map((_, entityIndex) => {
        return stackedData.reduce((sum, brand) => {
          return sum + (brand.data[entityIndex]?.y || 0);
        }, 0);
      });

      stackedData.forEach((brand) => {
        brand.data.forEach((point, idx) => {
          const total = totals[idx];
          point.y = safeDivide(point.rawValue || point.y, total) * 100;
        });
      });
    }

    return [
      {
        type: 'column' as const,
        data: stackedData,
        metricType: isPercentageView ? METRICTYPE.PERCENT : METRICTYPE.VOLUME,
        groupBy: selectedGroupByField.id
      }
    ];
  }, [
    groupedBrandData,
    theme.colors,
    totalEntityTrafficMap,
    totalComparisonEntityTrafficMap,
    isPercentageView,
    getBrandColor,
    selectedGroupByField.id,
    selectedTimePeriod
  ]);

  // Chart categories - StackedBarChartComponent props (default view)

  const chartCategories = useMemo(() => {
    const entityKeys = groupedBrandData?.[0]?.keywordOrder || [];

    if (selectedGroupByField.id === 'keyword') {
      return entityKeys.map((key) => {
        return { name: key, id: key, type: 'keyword' };
      });
    }

    return entityKeys.map((key) => {
      return { name: getEntityDisplayName(key), id: key, type: selectedGroupByField.id };
    });
  }, [groupedBrandData, selectedGroupByField.id, getEntityDisplayName]);

  // Chart title component
  const chartTitle = useMemo(() => {
    if (!showBrands) {
      return (
        <Flex gap="sm" alignItems="center" height="32px">
          {nonPercentageMetricDropdownOptions.length > 1 ? (
            <>
              <SlDropdownMenu
                buttonVariant="title"
                options={nonPercentageMetricDropdownOptions}
                defaultLabel=""
                onChange={(option) => {
                  const selectedOption = metricDropdownOptions.find((opt) => opt.id === option.id);

                  if (selectedOption) {
                    setSelectedMetricField(selectedOption);
                  }
                }}
                selectedId={selectedMetricField.id}
              />
              <Text variant="h4">by {selectedGroupByField.label}</Text>
            </>
          ) : (
            <>
              <Text variant="h4">
                {selectedMetricField.label} by {selectedGroupByField.label}
              </Text>
            </>
          )}
        </Flex>
      );
    }
    return (
      <Flex gap="sm" alignItems="center">
        <SlDropdownMenu
          buttonVariant="title"
          options={metricDropdownOptions}
          defaultLabel=""
          onChange={(option) => {
            const selectedOption = metricDropdownOptions.find((opt) => opt.id === option.id);
            if (selectedOption) {
              setSelectedMetricField(selectedOption);
              setIsPercentageView(selectedOption?.isPercentageVisible);
            }
          }}
          selectedId={selectedMetricField.id}
        />
        <Text variant="h4">by </Text>
        {groupByFieldOptions.length < 2 && <Text variant="h4">{selectedGroupByField.label}</Text>}
        {groupByFieldOptions.length >= 2 && (
          <SlDropdownMenu
            buttonVariant="title"
            options={groupByFieldOptions}
            defaultLabel=""
            onChange={(option) => {
              setSelectedGroupByField(option);
            }}
            selectedId={selectedGroupByField.id}
          />
        )}
      </Flex>
    );
  }, [
    showBrands,
    metricDropdownOptions,
    selectedMetricField.id,
    selectedMetricField.label,
    groupByFieldOptions,
    selectedGroupByField.label,
    selectedGroupByField.id,
    nonPercentageMetricDropdownOptions
  ]);

  // Custom tooltip builder
  const customTooltipBuilder = useCallback(
    ({ point, theme, tooltipCategoryTitle }) => {
      const pointIndex = point.index;
      const allSeries = chartSeries[0]?.data || [];
      const categoryName = chartCategories[pointIndex] ? chartCategories[pointIndex].name : '';

      // Add time period info to tooltip title
      const periodTitle =
        selectedTimePeriod === 'comparison'
          ? `${tooltipCategoryTitle} (${comparisonTimePeriodName})`
          : `${tooltipCategoryTitle} (${mainTimePeriodName})`;

      return ReactDOMServer.renderToStaticMarkup(
        <AllBrandsTooltip
          point={point}
          allSeries={allSeries}
          theme={theme}
          tooltipCategoryTitle={periodTitle}
          categoryName={categoryName}
          formatMetric={formatMetric}
          metricTypeToDisplay={selectedMetricField.metricTypeDisplay}
        />
      );
    },
    [
      chartSeries,
      chartCategories,
      formatMetric,
      selectedTimePeriod,
      mainTimePeriodName,
      comparisonTimePeriodName,
      selectedMetricField.metricTypeDisplay
    ]
  );

  // Non-brand view data preparation - Normal TopEntityChartV2 props
  const nonBrandCategories = useMemo(() => {
    if (selectedGroupByField.id === 'keyword') {
      if (entityTrafficData?.records?.length > 0) {
        return entityTrafficData.records.map((record) => ({
          name: getEntityDisplayName(record.fieldKey),
          id: record.fieldKey,
          type: 'keyword'
        }));
      }
      return [];
    } else {
      return entitiesForNonBrandChart.map((entity) => {
        return {
          name: getEntityDisplayName(entity.displayName),
          id: entity.fieldKey,
          type: selectedGroupByField.id
        };
      });
    }
  }, [selectedGroupByField.id, entityTrafficData, getEntityDisplayName, entitiesForNonBrandChart]);

  // Data generator for entity data
  const entityDataGenerator = useCallback(
    (dataSet: EntityRecord[]) => {
      const dataMap = new Map(dataSet.map((datum) => [String(datum.fieldKey), datum.value]));

      const sortedDataArray: [string, number][] = nonBrandCategories.map((category) => {
        const fieldKey = String(category.id);
        const value = dataMap.get(fieldKey) || 0;

        if (selectedGroupByField.id === 'keyword') {
          return [fieldKey, value];
        } else {
          return [getEntityDisplayName(category.id), value];
        }
      });

      return sortedDataArray;
    },
    [selectedGroupByField.id, getEntityDisplayName, nonBrandCategories]
  );

  // Props for TopEntityChartV2
  const getTopEntityChartV2Props = useCallback(() => {
    const defaultProps: TopEntityV2ChartProps = {
      chartSeries: [
        {
          type: 'column',
          color: theme.colors.secondary,
          data: entityDataGenerator(comparisonEntitiesForNonBrandChart),
          metricType: METRICTYPE.VOLUME,
          categories: nonBrandCategories
        } as SeriesColumnOptions,
        {
          type: 'column',
          color: theme.colors.primary,
          data: entityDataGenerator(entitiesForNonBrandChart),
          metricType: METRICTYPE.VOLUME,
          categories: nonBrandCategories
        } as SeriesColumnOptions
      ]
    };

    return defaultProps;
  }, [
    theme.colors.secondary,
    theme.colors.primary,
    entityDataGenerator,
    comparisonEntitiesForNonBrandChart,
    nonBrandCategories,
    entitiesForNonBrandChart
  ]);

  // Calculate loading state
  const isLoading =
    isLoadingKeywords ||
    isLoadingBrands ||
    (selectedGroupByField.id !== 'keyword' && isLoadingEntityTraffic) ||
    (selectedTimePeriod === 'current' && showBrands && isLoadingBrandEntityTraffic) ||
    (selectedTimePeriod === 'comparison' && showBrands && isLoadingComparisonBrandEntityTraffic) ||
    (showBrands && isLoadingComparisonEntityTraffic);

  // Handle chart export
  const { handleExportChart } = useStackedChartExportData({
    chartSeries,
    groupingType: selectedGroupByField.id as 'keyword' | 'category' | 'subcategory' | 'topic',
    totalEntityTrafficMap:
      selectedTimePeriod === 'comparison' ? totalComparisonEntityTrafficMap : totalEntityTrafficMap,
    groupedBrandData,
    getEntityDisplayName
  });

  const buildDropdownMenuOptions = useCallback(
    (series: SlDropdownMenuOption[]): SlDropdownMenuOption[] => {
      return [
        ...series,
        ...(userChangedBrands
          ? [
              {
                id: 'resetBrands',
                label: 'Reset brands'
              }
            ]
          : [])
      ];
    },
    [userChangedBrands]
  );

  const dropdownHandler = useCallback(
    (option: SlDropdownMenuOption, defaultDropdownHandler: () => Promise<void>) => {
      if (['resetBrands'].includes(option.id)) {
        pickTop4BrandsByVolume();
        setUserChangedBrands(false);
      } else {
        defaultDropdownHandler();
      }
    },
    [pickTop4BrandsByVolume, setUserChangedBrands]
  );

  const categoriesMetadata = useMemo(() => {
    return new Map(
      chartCategories.map((category) => [category.name, { name: category.name, id: category.id, type: category.type }])
    );
  }, [chartCategories]);

  return (
    <>
      <BeaconChartWithLegend
        title={chartTitle}
        {...(!showBrands && {
          primaryLegendProps: {
            displayName: mainTimePeriodDisplayName
          },
          comparisonLegendProps: {
            displayName: comparisonTimePeriodDisplayName
          }
        })}
        loading={isLoading}
        buildDropdownMenuOptions={buildDropdownMenuOptions}
        dropdownOptionHandler={dropdownHandler}
        loadingComponent={<SlSkeleton variant="rounded" height={574} width={1012} />}
        includeSummaryInfo={false}
        subtitle={
          <Flex marginTop="8px" marginBottom="32px" minHeight="32px">
            <Text variant="body1" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }} lineHeight="24px">
              The chart displays the {selectedMetricField.subtitleLabel} for{' '}
              {mainEntityType !== 'company' ? `${entityPhrase} ` : ''} in each {selectedGroupByField.id} {taxonomyText}{' '}
              during the{' '}
              {!showBrands ? (
                `${mainTimePeriodName} period compared to the ${comparisonTimePeriodName}.`
              ) : (
                <Flex sx={{ marginLeft: '-6px' }}>
                  <SlDropdownMenu
                    customEndIcon={<ChevronWithPeriod style={{ height: '24px', marginLeft: '1px' }} />}
                    options={[
                      { id: 'current', label: 'current time period' },
                      { id: 'comparison', label: 'comparison time period' }
                    ]}
                    buttonVariant="titleSmall"
                    selectedId={selectedTimePeriod}
                    defaultLabel=""
                    onChange={(option) => {
                      setSelectedTimePeriod(option.id as 'current' | 'comparison');
                    }}
                  />
                </Flex>
              )}
              <SubtitleFilters showFilterNumber={false} />
            </Text>
          </Flex>
        }
        convertSeriesToDelimitedData={async () => {
          try {
            const label = `Traffic by ${
              selectedGroupByField.id.charAt(0).toUpperCase() + selectedGroupByField.id.slice(1)
            }`;
            const timePeriodLabel =
              selectedTimePeriod === 'comparison' ? ` (${comparisonTimePeriodName})` : ` (${mainTimePeriodName})`;
            const nonBrandComparisonData = entityDataGenerator(comparisonEntitiesForNonBrandChart);
            const nonBrandCurrentData = entityDataGenerator(entitiesForNonBrandChart);
            if (showBrands) {
              const result = await handleExportChart();
              return result;
            } else {
              return saveBarChartAsDelimitedData(nonBrandCurrentData, nonBrandComparisonData, {
                fileTitle: `${label}${timePeriodLabel}`,
                metricLabel: label
              });
            }
          } catch (error) {
            console.error('Error converting series to delimited data', error);
            return null;
          }
        }}
      >
        <Flex gap={0} flexDirection="column" style={{ marginTop: '-32px' }}>
          <BrandFilterPills
            setShowBrands={(show) => {
              if (show) {
                setSelectedTimePeriod('current');
              } else {
                if (selectedMetricField.isPercentageVisible) {
                  setSelectedMetricField(
                    (prev) => metricDropdownOptions.find((option) => option.id === prev.defaultMetric) || prev
                  );
                }

                setIsPercentageView(false);
              }
              setShowBrands(show);
            }}
            showBrands={showBrands}
          />
          {showBrands ? (
            <StackedTopEntityChart
              maxNumberOfColumns={6}
              categories={chartCategories.map((category) => category.name)}
              metricType={
                isPercentageView ? METRICTYPE.PERCENT : selectedMetricField.metricTypeDisplay || METRICTYPE.VOLUME
              }
              chartSeries={chartSeries}
              categoriesMetadata={categoriesMetadata}
              tooltipCategoryTitle={isPercentageView ? 'PERCENTAGE' : 'TRAFFIC VOLUME'}
              customTooltipBuilder={customTooltipBuilder}
              chartOptionsOverride={(options) => {
                const updatedOptions = { ...options };
                updatedOptions.chart.marginLeft = 40;
                updatedOptions.chart.width = 1012;
                updatedOptions.chart.animation = false;

                // Disable animations for column series
                if (!updatedOptions.plotOptions) {
                  updatedOptions.plotOptions = {};
                }
                if (!updatedOptions.plotOptions.column) {
                  updatedOptions.plotOptions.column = {};
                }
                if (!updatedOptions.plotOptions.series) {
                  updatedOptions.plotOptions.series = {};
                }

                updatedOptions.plotOptions.column.animation = false;
                updatedOptions.plotOptions.series.animation = false;

                return updatedOptions;
              }}
            />
          ) : (
            <TopEntityChartV2 {...getTopEntityChartV2Props()} />
          )}
        </Flex>
      </BeaconChartWithLegend>
    </>
  );
};

export default AtlasV2FilterableStackedBarChart;
